import React, { useState } from 'react';

import { Button } from '$partials/ui';
import { SimpleImage } from '$partials/media';
import { useProperty } from '$contexts/property.jsx';
import IconChevron from '$partials/icons/icon-chevron';
import TeamMemberEnquireDialog from '$partials/properties/enquire-dialog.jsx';

/**
 * @param {Array<Array<any>>} arrays
 * @returns {Array<any>}
 */
const findFirstArrayWithItems = (arrays) => {
    return arrays.find((array) => (Array.isArray(array) && array.length > 0));
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').GetInTouch}
 */
const GetInTouch = () => {
    const { property } = useProperty();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // if (!property.primaryAgent) {
    //     return null;
    // }

    const agent = property?.primaryAgent;
    const office = property?.office;
    const propertyReferenceId = property?.propertyReferenceId;
    const pageTitle = property?.pageTitle;
    const pageUrl = property?.pageUrl;
    const whatsappMessage = `I am interested in: ${[pageTitle, pageUrl].filter((value) => Boolean(value)).join(' ')}`.trim();

    const displayEmailAddresses = findFirstArrayWithItems([
        office?.newHomesDisplayEmailAddresses,
        office?.lettingsDisplayEmailAddresses,
        office?.salesDisplayEmailAddresses,
        office?.generalDisplayEmailAddresses
    ]);

    const displayPhoneNumbers = findFirstArrayWithItems([
        office?.newHomesDisplayPhoneNumbers,
        office?.lettingsDisplayPhoneNumbers,
        office?.salesDisplayPhoneNumbers,
        office?.generalDisplayPhoneNumbers
    ]);

    const displayWhatsapp = findFirstArrayWithItems([
        office?.newHomesDisplayWhatsapp,
        office?.lettingsDisplayWhatsapp,
        office?.salesDisplayWhatsapp,
        office?.generalDisplayWhatsapp
    ]);

    return (
        <div className='module-get-in-touch bg-main-background'>
            <div className='layout-container layout-site-outer-padding py-24 sm:py-48 md:py-64'>

                <div className='md:hidden block md:w-4/12 mb-32 sm:mb-48'>
                    <h2 className='type-style-2'>Get in Touch</h2>
                </div>

                <div className='flex flex-col sm:flex-row col-wrapper gap-y-24'>
                    <div className='hidden md:block col-padding md:w-4/12'>
                        <h2 className='type-style-2'>Get in Touch</h2>
                    </div>

                    <div className='col-padding flex flex-col sm:flex-row gap-y-16 gap-x-24 sm:w-2/3 md:w-4/12'>
                        {agent?.profileImage && (
                            <div className='shrink-0 w-[112px] h-[112px] sm:w-[160px] sm:h-[160px] rounded overflow-hidden'>
                                <SimpleImage
                                    {...agent.profileImage}
                                    className='w-full h-full object-cover' />
                            </div>
                        )}

                        <div className='flex flex-col justify-between h-full grow'>
                            <div className='flex flex-col gap-8 shrink-0'>
                                <h3 className='type-style-3 sm:type-style-4'>
                                    {agent?.name}
                                </h3>

                                {(agent?.position || office?.title) && (
                                    <p className='type-style-eyebrow'>
                                        {[agent?.position, office?.title].filter((t) => Boolean(t)).join(' / ')}
                                    </p>
                                )}

                                {propertyReferenceId && (
                                    <p className='type-style-eyebrow'>
                                        {`Property Ref: ${propertyReferenceId}`}
                                    </p>
                                )}

                                <div className='hidden sm:block mt-20'>
                                    <Button
                                        className='w-full sm:w-max button-brand-filled button-brand-filled--small sm:py-7 sm:px-12 md:py-13 md:px-35 rounded type-style-eyebrow'
                                        onClick={() => setIsDialogOpen(true)}>
                                        Enquire
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='col-padding w-full sm:w-1/3 md:w-4/12'>
                        <div className='flex flex-col type-style-eyebrow'>
                            {displayPhoneNumbers?.map((phone, i) => {
                                return (
                                    <div
                                        className='border-t border-main-border py-16'
                                        key={i}>
                                        <a
                                            className='flex items-center justify-between w-full'
                                            href={`tel:${phone.replace(/[^\+\d]/g, '')}`}>
                                            <span>
                                                {`Tel: ${phone}`}
                                            </span>
                                            <IconChevron className='inline-block ml-5.5 -rotate-90' />
                                        </a>
                                    </div>
                                );
                            })}

                            {displayWhatsapp?.map((whatsapp, i) => {
                                return (
                                    <div
                                        className='border-t border-main-border py-16'
                                        key={i}>
                                        <a
                                            className='flex items-center justify-between w-full'
                                            href={`https://wa.me/${whatsapp.replace(/[^\+\d]/g, '')}?text=${encodeURIComponent(whatsappMessage)}`}>
                                            <span>
                                                {`Whatsapp: ${whatsapp}`}
                                            </span>
                                            <IconChevron className='inline-block ml-5.5 -rotate-90' />
                                        </a>
                                    </div>
                                );
                            })}

                            {displayEmailAddresses?.map((email, i) => {
                                return (
                                    <div
                                        className='border-t border-main-border py-16'
                                        key={i}>
                                        <a
                                            className='flex items-center justify-between w-full'
                                            href={`mailto:${email}`}>
                                            <span>
                                                {`Email: ${email}`}
                                            </span>
                                            <IconChevron className='inline-block ml-5.5 -rotate-90' />
                                        </a>
                                    </div>
                                );
                            })}
                        </div>

                        <div className='block sm:hidden'>
                            <Button
                                className='mt-16 w-full button-brand-filled py-13 rounded type-style-eyebrow'
                                onClick={() => setIsDialogOpen(true)}>
                                Enquire
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {isDialogOpen && (
                <TeamMemberEnquireDialog
                    closeDialog={() => setIsDialogOpen(false)}
                    isOpen={isDialogOpen} />
            )}
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default GetInTouch;
